
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";

export function AccountSettings() {
  const [newUsername, setNewUsername] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [userInitial, setUserInitial] = useState("");
  const [username, setUsername] = useState("");
  const [isUpdatingUsername, setIsUpdatingUsername] = useState(false);
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const getUser = async () => {
      if (user?.email) {
        setUserEmail(user.email);
        setUserInitial(user.email.charAt(0).toUpperCase());

        // Get the user's avatar if they signed in with Google
        if (user.app_metadata?.provider === 'google') {
          setAvatarUrl(user.user_metadata?.avatar_url || "");
        }

        // Get the user's profile
        const { data: profile } = await supabase
          .from("profiles")
          .select("username, avatar_url")
          .eq("id", user.id)
          .single();

        if (profile?.username) {
          setUsername(profile.username);
          setNewUsername(profile.username);
        }
        
        if (profile?.avatar_url) {
          setAvatarUrl(profile.avatar_url);
        }
      }
    };
    getUser();
  }, [user]);

  const handleUpdateUsername = async () => {
    try {
      if (!user) throw new Error("No user found");
      setIsUpdatingUsername(true);

      if (!newUsername.trim()) {
        throw new Error("Username cannot be empty");
      }

      // First check if the profile exists
      const { data: profile } = await supabase
        .from("profiles")
        .select()
        .eq("id", user.id)
        .single();

      if (!profile) {
        // Create a new profile if it doesn't exist
        const { error: insertError } = await supabase.from("profiles").insert([
          {
            id: user.id,
            username: newUsername,
            user_type: "basic",
          },
        ]);
        if (insertError) throw insertError;
      } else {
        // Update existing profile
        const { error: updateError } = await supabase
          .from("profiles")
          .update({
            username: newUsername,
          })
          .eq("id", user.id);

        if (updateError) {
          if (updateError.code === "23505") {
            // Unique constraint violation
            throw new Error("Username already taken");
          }
          throw updateError;
        }
      }

      setUsername(newUsername);
      toast.success("Username updated successfully");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to update username");
    } finally {
      setIsUpdatingUsername(false);
    }
  };

  const handleResetPassword = async () => {
    try {
      if (!user?.email) throw new Error("No email found");
      setIsSendingReset(true);

      const { error } = await supabase.auth.resetPasswordForEmail(user.email);
      if (error) throw error;

      toast.success("Password reset email sent");
    } catch (error) {
      toast.error("Failed to send password reset email");
    } finally {
      setIsSendingReset(false);
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    try {
      setIsUploading(true);

      // Check file size and type
      if (file.size > 2 * 1024 * 1024) {
        throw new Error("File size should be less than 2MB");
      }

      if (!file.type.startsWith('image/')) {
        throw new Error("Only image files are allowed");
      }

      // Upload file to storage
      const fileExt = file.name.split('.').pop();
      const filePath = `avatars/${user.id}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('profiles')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('profiles')
        .getPublicUrl(filePath);

      const avatarUrl = urlData.publicUrl;

      // Update profile with new avatar URL
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: avatarUrl })
        .eq('id', user.id);

      if (updateError) throw updateError;

      setAvatarUrl(avatarUrl);
      toast.success("Profile picture updated");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to upload avatar");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <DialogContent className="sm:max-w-md bg-gray-900/95 border border-gray-800 rounded-3xl">
      <DialogHeader>
        <DialogTitle className="text-white/90">Account Settings</DialogTitle>
      </DialogHeader>
      <div className="space-y-6 py-4">
        <div className="flex items-center gap-4">
          <div className="relative group">
            <Avatar className="h-20 w-20 border-2 border-sky-500/20 rounded-2xl">
              <AvatarImage src={avatarUrl} alt="User avatar" />
              <AvatarFallback className="bg-sky-500/20 text-sky-200 rounded-2xl">
                {userInitial}
              </AvatarFallback>
            </Avatar>
            
            <label className="absolute inset-0 flex items-center justify-center bg-black/50 text-white text-xs rounded-2xl opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity">
              {isUploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Change"
              )}
              <input
                type="file"
                className="hidden"
                accept="image/*"
                onChange={handleAvatarUpload}
                disabled={isUploading}
              />
            </label>
          </div>
          
          <div>
            <h4 className="font-medium text-white/90">Profile Picture</h4>
            <p className="text-sm text-gray-400">
              Upload a custom avatar or use your Google profile picture
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="email" className="text-white/80">
            Email
          </Label>
          <Input
            id="email"
            value={userEmail}
            disabled
            className="bg-white/5 border-white/10 text-white/80 rounded-xl"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="username" className="text-white/80">
            Username
          </Label>
          <div className="flex gap-2">
            <Input
              id="username"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              placeholder={username || "Enter new username"}
              className="bg-white/5 border-white/10 text-white/80 placeholder:text-gray-500 rounded-xl"
            />
            <Button
              onClick={handleUpdateUsername}
              disabled={isUpdatingUsername}
              className="bg-sky-500 hover:bg-sky-600 text-white rounded-xl px-4 min-w-[90px]"
            >
              {isUpdatingUsername ? (
                <Loader2 className="h-4 w-4 animate-spin mx-auto" />
              ) : (
                "Update"
              )}
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-white/80">Password</Label>
          <Button
            variant="outline"
            className="w-full border-white/10 text-white/80 hover:bg-white/5 rounded-xl"
            onClick={handleResetPassword}
            disabled={isSendingReset}
          >
            {isSendingReset ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Sending...
              </>
            ) : (
              "Reset Password"
            )}
          </Button>
        </div>
      </div>
    </DialogContent>
  );
}
